/**
 * sunbaeBlog — 커뮤니티(/community) 데이터. 네이버 블로그 RSS를 Cloudflare Worker가 JSON으로 제공.
 *   GET  /            → { posts: [...] }            글 목록(KV 전체 저장분)
 *   GET  /stats       → { stats: { id: { hits, hearts, week } } }  홈페이지 조회수·좋아요(D1)
 *   GET  /post?id=    → { render?: 'a'|'b', html, css, fix | viewUrl | blocks }
 *   POST /hit?id=     → 홈페이지 조회수 +1
 *   POST /like?id=&on=1|0
 * 포팅 원본: claude.ai/design `헤더+칼럼.dc.html`
 */

export const BLOG_API = "https://sunbae-blog.peetsunbae.workers.dev";

export const COMMUNITY_TABS = ["전체", "재원생 후기", "선배 이야기", "멘토 칼럼", "주간 밥상"] as const;

export interface RawPost {
    id: string;
    cat?: string;
    blogCat?: string;
    title: string;
    summary?: string;
    author?: string;
    img?: string;
    url?: string;
    date: string;
    likes?: number;
    cmts?: number;
    views?: number;
}

export interface PostStat { hits?: number; hearts?: number; week?: number; }

export interface CommunityPost {
    id: string;
    cat: string;
    title: string;
    summary: string;
    author: string;
    img: string;
    url: string;
    date: string;
    likes: number;
    cmts: number;
    /** 블로그 조회수 + 홈페이지 조회수 */
    views: number;
    /** 홈페이지 좋아요 */
    hearts: number;
    /** 전 주 월~일 홈페이지 조회수 */
    week: number;
}

export type PostBlock = { t: "p" | "h" | "quote" | "cap" | "img" | "hr" | "link"; text?: string; src?: string; url?: string };

export type PostBody =
    | { loading: true }
    | { render: "a"; html: string; css: string; fix: string; title?: string }
    | { render: "b"; viewUrl: string; title?: string }
    | { blocks: PostBlock[]; title?: string }
    | { preview: true };

export const fetchPosts = (): Promise<RawPost[] | null> =>
    fetch(BLOG_API)
        .then((r) => r.json())
        .then((j) => (j && Array.isArray(j.posts) ? j.posts : null))
        .catch(() => null);

export const fetchStats = (): Promise<Record<string, PostStat> | null> =>
    fetch(BLOG_API + "/stats")
        .then((r) => r.json())
        .then((j) => (j && j.stats ? j.stats : null))
        .catch(() => null);

/** 열 때마다 새로 요청 (서버에서 render 방식이 바뀌어도 바로 반영). /post 실패 시 미리보기 */
export const fetchPostBody = (id: string): Promise<PostBody> =>
    fetch(BLOG_API + "/post?id=" + encodeURIComponent(id), { cache: "no-cache" })
        .then((r) => r.json())
        .then((j): PostBody => {
            const r = j && j.render;
            if (r === "a" && j.html) return { render: "a", html: j.html, css: j.css || "", fix: j.fix || "", title: j.title };
            if (r === "b" && j.viewUrl) return { render: "b", viewUrl: j.viewUrl, title: j.title };
            return j && j.blocks && j.blocks.length ? { blocks: j.blocks, title: j.title } : { preview: true };
        })
        .catch((): PostBody => ({ preview: true }));

const beacon = (u: string) => {
    try {
        if (navigator.sendBeacon) navigator.sendBeacon(u);
        else fetch(u, { method: "POST", keepalive: true });
    } catch (e) { /* noop */ }
};

/** 홈페이지 조회수 +1. 같은 글은 한 세션에 1번만 */
export const sendHit = (id: string) => {
    try {
        const k = "sunbae-hit-" + id;
        if (sessionStorage.getItem(k)) return;
        sessionStorage.setItem(k, "1");
    } catch (e) { return; }
    beacon(BLOG_API + "/hit?id=" + encodeURIComponent(id));
};

export const sendLike = (id: string, on: boolean) =>
    beacon(BLOG_API + "/like?id=" + encodeURIComponent(id) + "&on=" + (on ? 1 : 0));

/* 작성자 표시(Worker author가 없을 때만): 멘토 칼럼 = 본문·제목의 "OOO 멘토" / 그 외 '수능선배'.
   재원생 후기는 Worker가 글마다 가명(신O기)을 D1에 고정 배정해 내려주므로, 없을 때 지점명을 노출하지 않고 '수능선배' */
const NOT_NAME = ["수능선배", "대학생", "담임", "전담", "선배", "우리", "학습", "입시", "전문", "합격생", "의대생"];
export const decodeEntities = (s: string) =>
    String(s || "").replace(/&#x27;|&#39;/g, "'").replace(/&quot;|&#x22;/g, '"').replace(/&amp;/g, "&");

const authorOf = (p: RawPost) => {
    const t = decodeEntities(p.title), s = decodeEntities(p.summary || "");
    if (p.cat === "멘토 칼럼") {
        const re = /([가-힣]{2,4})\s?멘토/g;
        for (const src of [s, t]) {
            re.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = re.exec(src))) if (NOT_NAME.indexOf(m[1]) === -1) return m[1] + " 멘토";
        }
    }
    return "수능선배";
};

/** Worker 글 목록 + /stats 합치기. 식단표는 공지사항 > 식단표 소속이라 제외 */
export const mergePosts = (raw: RawPost[], stats: Record<string, PostStat>): CommunityPost[] =>
    raw
        .filter((p) => p.cat && p.blogCat !== "수능선배 식단표")
        .map((p) => {
            const st = stats[p.id];
            return {
                id: p.id,
                cat: p.cat as string,
                title: p.title,
                summary: p.summary || "",
                author: p.author || authorOf(p),
                img: p.img || "",
                url: p.url || "",
                date: p.date,
                likes: p.likes || 0,
                cmts: p.cmts || 0,
                views: (Number(p.views) || 0) + (st ? Number(st.hits) || 0 : 0),
                hearts: st ? Number(st.hearts) || 0 : 0,
                week: st ? Number(st.week) || 0 : 0,
            };
        });

export const fmtN = (n: number) => {
    n = n || 0;
    return n >= 10000 ? Math.round(n / 1000) / 10 + "만" : n.toLocaleString("ko-KR");
};

/** 최근 7일 글 = N 배지 (date 'YYYY.MM.DD' 문자열 비교) */
export const newFrom = () => {
    const d = new Date(Date.now() - 7 * 864e5);
    return d.getFullYear() + "." + String(d.getMonth() + 1).padStart(2, "0") + "." + String(d.getDate()).padStart(2, "0");
};

/** 정렬 키 내림차순, 같으면 공감·댓글·최신순 */
export const byKey = (k: "views" | "week") => (a: CommunityPost, b: CommunityPost) =>
    (b[k] - a[k]) || (b.likes - a.likes) || (b.cmts - a.cmts) || (a.date < b.date ? 1 : -1);

export const byNewest = (a: CommunityPost, b: CommunityPost) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0);

/** 주간 인기 글 8개: 재원생 후기·선배 이야기·멘토 칼럼 각 2개 보장 + 나머지 상위 (주간 밥상 제외) */
export const pickBest = (posts: CommunityPost[], total = 8) => {
    const cmp = byKey("week");
    const cats = ["재원생 후기", "선배 이야기", "멘토 칼럼"];
    const base = cats.reduce<CommunityPost[]>(
        (acc, c) => acc.concat(posts.filter((r) => r.cat === c).sort(cmp).slice(0, 2)),
        []
    );
    const rest = posts.filter((r) => cats.indexOf(r.cat) !== -1 && base.indexOf(r) === -1).sort(cmp);
    return base.concat(rest.slice(0, Math.max(0, total - base.length))).sort(cmp);
};

/* 좋아요: 이 기기에서 누른 글은 localStorage에 기억 (낙관적 반영).
   누른 직후 90초 동안은 '누르기 전 숫자 ±1'을 보여줌 (/stats 60초 캐시로 되돌아가는 것 방지) */
export type LikePend = Record<string, { target: number; at: number }>;
export const LIKE_PEND_MS = 90000;

const readJson = <T,>(k: string, fb: T): T => {
    try {
        const v = JSON.parse(localStorage.getItem(k) || "null");
        return v && typeof v === "object" ? v : fb;
    } catch (e) { return fb; }
};
export const loadLiked = () => readJson<Record<string, 1>>("sunbae-liked", {});
export const loadLikePend = (): LikePend => {
    const all = readJson<LikePend>("sunbae-like-pend", {});
    const live: LikePend = {};
    Object.keys(all).forEach((k) => { if (all[k] && Date.now() - all[k].at < LIKE_PEND_MS) live[k] = all[k]; });
    return live;
};
export const saveLikes = (liked: Record<string, 1>, pend: LikePend) => {
    try {
        localStorage.setItem("sunbae-like-pend", JSON.stringify(pend));
        localStorage.setItem("sunbae-liked", JSON.stringify(liked));
    } catch (e) { /* noop */ }
};
export const heartsShown = (pend: LikePend, id: string, server: number) => {
    const pd = pend[id];
    if (pd && Date.now() - pd.at < LIKE_PEND_MS && server !== pd.target) return pd.target;
    return server;
};
